import LeadForm from '@/components/shared/LeadForm';
import { useMeta } from '@/hooks/useMeta';
import { MapPin, Phone, Mail, Clock } from 'lucide-react';

const CONTACT_INFO = [
  { icon: MapPin, title: 'Visit Us', detail: '3101 Park Blvd, 1st Floor, Palo Alto, CA 94306' },
  { icon: Phone, title: 'Call Us', detail: '+1 (877) 606-1381' },
  { icon: Mail, title: 'Email Us', detail: 'info@americantrademarkfirm.com' },
  { icon: Clock, title: 'Working Hours', detail: 'Mon - Fri: 9AM - 6PM EST' },
];

export default function ContactUs() {
  useMeta({
    title: 'Contact Us | Get Expert Trademark Help | AmericanTrademark Firm',
    description: 'Contact AmericanTrademark Firm for trademark registration, copyright protection, and IP services. Our expert team responds within 24 hours. Call, email, or fill out our form.',
    keywords: 'contact AmericanTrademark Firm, trademark help, IP consultation, trademark registration support, brand protection inquiry',
  });

  return (
    <div data-testid="contact-page">
      {/* Hero */}
      <section className="relative py-24 md:py-36 overflow-hidden" data-testid="contact-hero">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1768270181430-3e3672a32283?w=1920&q=80)' }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#09090B]/80 via-[#09090B]/65 to-[#09090B]/40" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#6B9FFF] mb-4">Contact Us</p>
            <h1 className="font-heading font-black text-5xl md:text-6xl tracking-tighter leading-none text-white mb-6">
              Get in Touch
            </h1>
            <p className="text-lg md:text-xl text-zinc-300 font-body leading-relaxed">
              Have questions about our services? Ready to start your trademark registration? Our team is here to help.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form + Info */}
      <section className="py-24 md:py-32 bg-white" data-testid="contact-form-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-12 gap-12">
            <div className="md:col-span-5">
              <h2 className="font-heading font-extrabold text-3xl tracking-tight text-[#09090B] mb-6">
                Contact Information
              </h2>
              <p className="text-base text-zinc-600 font-body leading-relaxed mb-8">
                Reach out to us through any of the channels below. We typically respond within 24 hours on business days.
              </p>
              <div className="space-y-6">
                {CONTACT_INFO.map((item, idx) => {
                  const Icon = item.icon;
                  return (
                    <div key={idx} className="flex items-start gap-4" data-testid={`contact-info-${idx}`}>
                      <div className="w-10 h-10 bg-[#F4F4F5] flex items-center justify-center shrink-0">
                        <Icon className="w-5 h-5 text-[#002FA7]" />
                      </div>
                      <div>
                        <p className="font-heading font-bold text-[#09090B] text-sm">{item.title}</p>
                        <p className="text-sm text-zinc-600 font-body">{item.detail}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="md:col-span-7">
              <div className="border-2 border-[#09090B] shadow-[8px_8px_0px_rgba(9,9,11,1)] p-8" data-testid="contact-form-container">
                <h3 className="font-heading font-bold text-xl text-[#09090B] mb-2">Send Us a Message</h3>
                <p className="text-sm text-zinc-500 font-body mb-6">Fill in the form below and we'll get back to you shortly.</p>
                <LeadForm buttonText="Send Message" />
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
